import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { Logo } from "@/components/logo";
import logoLight from "@/assets/capitaldart-logo-light.svg.asset.json";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { FAQ_CATEGORIES } from "@/lib/faq-content";
import { isValidElement, type ReactNode } from "react";

// Plain-text extraction so JSON-LD matches the visible answer copy exactly.
function faqAnswerToText(node: ReactNode): string {
  if (node == null || typeof node === "boolean") return "";
  if (typeof node === "string") return node;
  if (typeof node === "number") return String(node);
  if (Array.isArray(node)) return node.map(faqAnswerToText).join("");
  if (isValidElement<{ children?: ReactNode }>(node)) {
    if (node.type === "br") return "\n";
    return faqAnswerToText(node.props.children);
  }
  return "";
}

const FAQ_JSON_LD = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: FAQ_CATEGORIES.flatMap((cat) =>
    cat.items.map((item) => ({
      "@type": "Question",
      name: item.q,
      acceptedAnswer: {
        "@type": "Answer",
        text: faqAnswerToText(item.a)
          .replace(/\s+/g, " ")
          .replace(/\s+([.,;:!?])/g, "$1")
          .trim(),
      },
    })),
  ),
};
import { MobileNav } from "@/components/marketing/mobile-nav";
import { useAuth } from "@/hooks/use-auth";

const LOGO_LIGHT = logoLight.url;

const NAVY = "#0B2545";
const NAVY_DEEP = "#07182E";
const PAPER = "#F4F1E8";
const PAPER_2 = "#FBFAF5";
const GOLD = "#C9A24B";
const INK = "#14213A";
const INK_2 = "#545C6B";
const INK_MUTED = "#6B7384";
const LINE = "#E4DECF";
const NAVY_TEXT = "#EDEAE0";
const NAVY_TEXT_DIM = "#B6BFCD";

const FONT_SERIF = "'Spectral', Georgia, serif";
const FONT_SANS = "'IBM Plex Sans', system-ui, sans-serif";
const FONT_MONO = "'IBM Plex Mono', ui-monospace, monospace";

export const Route = createFileRoute("/faq")({
  head: () => ({
    meta: [
      { title: "FAQ: CapitalDart" },
      {
        name: "description",
        content:
          "Answers about CapitalDart: how it differs from placement agents, databases, and AI tools; how matching, credits, teams, and privacy work.",
      },
      { property: "og:title", content: "FAQ: CapitalDart" },
      {
        property: "og:description",
        content:
          "Answers about CapitalDart: how it differs from placement agents, databases, and AI tools; how matching, credits, teams, and privacy work.",
      },
      { property: "og:url", content: "https://capitaldart.com/faq" },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: "FAQ: CapitalDart" },
      {
        name: "twitter:description",
        content:
          "Answers about CapitalDart: how it differs from placement agents, databases, and AI tools; how matching, credits, teams, and privacy work.",
      },
    ],
    links: [{ rel: "canonical", href: "https://capitaldart.com/faq" }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify(FAQ_JSON_LD),
      },
    ],
  }),
  component: FaqPage,
});

function FaqPage() {
  return (
    <main
      style={{ background: PAPER, color: INK, fontFamily: FONT_SANS }}
      className="min-h-screen antialiased"
    >
      <FaqNav />
      <section
        style={{ background: PAPER_2, padding: "88px 0 56px", borderBottom: `1px solid ${LINE}` }}
      >
        <div className="mx-auto" style={{ maxWidth: 980, padding: "0 32px" }}>
          <div
            style={{
              fontFamily: FONT_MONO,
              fontSize: 13,
              letterSpacing: "0.16em",
              textTransform: "uppercase",
              color: GOLD,
              marginBottom: 18,
            }}
          >
            Frequently asked questions
          </div>
          <h1
            style={{
              fontFamily: FONT_SERIF,
              fontWeight: 500,
              fontSize: "clamp(36px,4.2vw,56px)",
              lineHeight: 1.05,
              letterSpacing: "-.01em",
              margin: 0,
            }}
          >
            Questions, answered.
          </h1>
          <p
            style={{
              marginTop: 18,
              maxWidth: 680,
              color: INK_2,
              fontSize: 17,
              lineHeight: 1.6,
            }}
          >
            How CapitalDart fits next to placement agents, databases, and other AI tools, plus how
            matching, credits, teams, and privacy work.
          </p>
        </div>
      </section>

      <section style={{ padding: "72px 0 96px" }}>
        <div className="mx-auto" style={{ maxWidth: 980, padding: "0 32px" }}>
          {FAQ_CATEGORIES.map((cat) => (
            <div key={cat.heading} style={{ marginBottom: 56 }}>
              <h2
                style={{
                  fontFamily: FONT_MONO,
                  fontWeight: 500,
                  fontSize: 13,
                  letterSpacing: "0.18em",
                  textTransform: "uppercase",
                  color: GOLD,
                  margin: "0 0 18px",
                  paddingBottom: 14,
                  borderBottom: `2px solid ${GOLD}`,
                }}
              >
                {cat.heading}
              </h2>
              <Accordion type="single" collapsible className="w-full">
                {cat.items.map((item, i) => (
                  <AccordionItem
                    key={item.q}
                    value={`${cat.heading}-${i}`}
                    className="border-b"
                    style={{ borderColor: LINE }}
                  >
                    <AccordionTrigger
                      className="hover:no-underline"
                      style={{
                        fontFamily: FONT_SERIF,
                        fontWeight: 600,
                        fontSize: 20,
                        lineHeight: 1.3,
                        color: INK,
                        paddingTop: 20,
                        paddingBottom: 20,
                      }}
                    >
                      {item.q}
                    </AccordionTrigger>
                    <AccordionContent
                      style={{
                        fontFamily: FONT_SANS,
                        fontSize: 16,
                        lineHeight: 1.7,
                        color: INK_2,
                        paddingBottom: 24,
                        paddingRight: 32,
                      }}
                    >
                      {item.a}
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </div>
          ))}

          <div
            style={{
              marginTop: 24,
              display: "flex",
              flexWrap: "wrap",
              gap: 28,
              alignItems: "center",
            }}
          >
            <Link
              to="/compare"
              style={{
                fontSize: 15,
                fontWeight: 600,
                color: INK,
                borderBottom: `2px solid ${GOLD}`,
                paddingBottom: 3,
                textDecoration: "none",
                fontFamily: FONT_SANS,
              }}
            >
              Compare CapitalDart to placement agents, databases &amp; AI tools →
            </Link>
            <Link
              to="/"
              hash="contact"
              style={{
                fontSize: 15,
                fontWeight: 600,
                color: INK,
                borderBottom: `2px solid ${GOLD}`,
                paddingBottom: 3,
                textDecoration: "none",
                fontFamily: FONT_SANS,
              }}
            >
              Still have a question? Contact us →
            </Link>
          </div>
        </div>
      </section>

      <FaqFooter />
    </main>
  );
}

function FaqNav() {
  const { session, loading, signOut } = useAuth();
  const navigate = useNavigate();
  const isAuthed = Boolean(session);
  return (
    <header
      style={{
        background: NAVY,
        borderBottom: `1px solid rgba(255,255,255,0.06)`,
        position: "relative",
      }}
    >
      <style>{`
        @media (max-width: 1023.98px) {
          .cd-faqnav-desktop { display: none !important; }
          .cd-faqnav-mobile { display: inline-flex !important; }
        }
      `}</style>
      <div
        className="mx-auto"
        style={{
          maxWidth: 1200,
          padding: "18px 32px",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <Link to="/" style={{ display: "inline-flex", alignItems: "center", gap: 10 }}>
          <Logo
            src={LOGO_LIGHT}
            alt="CapitalDart"
            height={34}
            style={{ display: "block" }}
            trademarkClassName="text-[#F4F1E8]"
          />
        </Link>
        <nav
          className="cd-faqnav-desktop"
          style={{ display: "flex", alignItems: "center", gap: 28 }}
        >
          {loading ? null : isAuthed ? (
            <>
              <Link
                to="/dashboard"
                style={{
                  fontFamily: FONT_SANS,
                  fontSize: 14,
                  fontWeight: 600,
                  background: GOLD,
                  color: NAVY_DEEP,
                  padding: "10px 18px",
                  borderRadius: 6,
                  textDecoration: "none",
                }}
              >
                Dashboard
              </Link>
              <button
                type="button"
                onClick={async () => {
                  await signOut();
                  navigate({ to: "/" });
                }}
                style={{
                  fontFamily: FONT_SANS,
                  fontSize: 14,
                  color: NAVY_TEXT_DIM,
                  background: "transparent",
                  border: "none",
                  padding: 0,
                  cursor: "pointer",
                }}
              >
                Sign out
              </button>
            </>
          ) : (
            <>
              <Link
                to="/"
                style={{
                  fontFamily: FONT_SANS,
                  fontSize: 14,
                  color: NAVY_TEXT_DIM,
                  textDecoration: "none",
                }}
              >
                Back to home
              </Link>
              <Link
                to="/auth"
                search={{ mode: "signup" as const }}
                style={{
                  fontFamily: FONT_SANS,
                  fontSize: 14,
                  fontWeight: 600,
                  background: GOLD,
                  color: NAVY_DEEP,
                  padding: "10px 18px",
                  borderRadius: 6,
                  textDecoration: "none",
                }}
              >
                Get started
              </Link>
            </>
          )}
        </nav>
        <MobileNav className="cd-faqnav-mobile" />
      </div>
    </header>
  );
}

function FaqFooter() {
  return (
    <footer
      style={{ background: NAVY_DEEP, color: NAVY_TEXT, padding: "48px 0", textAlign: "center" }}
    >
      <div className="mx-auto" style={{ maxWidth: 1200, padding: "0 32px" }}>
        <Logo
          src={LOGO_LIGHT}
          alt="CapitalDart"
          height={26}
          style={{ display: "inline-block", marginBottom: 14 }}
          trademarkClassName="text-[#F4F1E8]"
        />
        <div style={{ fontFamily: FONT_SANS, fontSize: 13, color: "#7E8A9C" }}>
          © {new Date().getFullYear()} CapitalDart. All rights reserved.
        </div>
      </div>
    </footer>
  );
}
